/**
 * InlineCitationBadge — clickable pill that renders inside an assistant
 * message. Two interactions:
 *  - HOVER → fetches a preview from /api/legal/jurisprudence/search and
 *    shows the top match (court / number / date / principle) in a HoverCard
 *  - CLICK → opens the full Jurisprudence search page pre-populated with
 *    this citation
 *
 * Hover-preview cache is shared across badges in the same render tree to
 * avoid duplicate fetches when the same ruling is cited multiple times.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { api } from "@/lib/api";
import { ExternalLink, Scale, BookOpen, FileText, Loader2 } from "lucide-react";

// Module-level cache: { query → Promise<topMatch | null> }. Reset on hot-reload.
const _previewCache = new Map();

function fetchPreview(query) {
  if (_previewCache.has(query)) return _previewCache.get(query);
  const p = api.get(`/legal/jurisprudence/search?q=${encodeURIComponent(query)}&limit=1`)
    .then((r) => {
      const top = (r.data && r.data.results && r.data.results[0]) || null;
      return top;
    })
    .catch(() => null);
  _previewCache.set(query, p);
  return p;
}

const COLORS = {
  ruling: {
    bg: "bg-[#D4A937]/15 hover:bg-[#D4A937]/25",
    text: "text-[#A67C0F] dark:text-[#F4C955]",
    border: "border-[#D4A937]/30",
    icon: Scale,
  },
  law: {
    bg: "bg-[#0B0E5C]/10 hover:bg-[#0B0E5C]/20",
    text: "text-[#1A1D7A] dark:text-[#A6AAE8]",
    border: "border-[#0B0E5C]/25",
    icon: FileText,
  },
  code: {
    bg: "bg-[#9ACD32]/15 hover:bg-[#9ACD32]/25",
    text: "text-[#5C7A1F] dark:text-[#B8DC68]",
    border: "border-[#9ACD32]/30",
    icon: BookOpen,
  },
};

export function InlineCitationBadge({ text, kind = "ruling", query, dataTestId }) {
  const navigate = useNavigate();
  const c = COLORS[kind] || COLORS.ruling;
  const Icon = c.icon;
  const [preview, setPreview] = useState(undefined); // undefined = not yet loaded
  const [loading, setLoading] = useState(false);

  const handleClick = (e) => {
    if (e && typeof e.stopPropagation === "function") e.stopPropagation();
    const q = encodeURIComponent(query || text);
    navigate(`/legal/jurisprudence?q=${q}`);
  };

  const handleHoverStart = () => {
    if (preview !== undefined || loading) return;
    setLoading(true);
    fetchPreview(query || text)
      .then((top) => setPreview(top))
      .finally(() => setLoading(false));
  };

  return (
    <HoverCard openDelay={300} closeDelay={100}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          onClick={handleClick}
          onMouseEnter={handleHoverStart}
          onFocus={handleHoverStart}
          className={`mx-0.5 inline-flex items-baseline gap-1 rounded-md border px-1.5 py-0.5 align-baseline font-mono text-[12px] font-medium transition-colors ${c.bg} ${c.text} ${c.border}`}
          title={`Apri ${text} nel database Giurisprudenza`}
          data-testid={dataTestId || `inline-citation-${kind}`}
        >
          <Icon className="h-3 w-3 self-center" />
          <span>{text}</span>
          <ExternalLink className="h-2.5 w-2.5 self-center opacity-60" />
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        className="w-96 border-slate-700 bg-slate-900/95 text-slate-100 shadow-xl backdrop-blur"
        side="top"
        align="start"
        data-testid={`citation-preview-${query || text}`}
      >
        {loading || preview === undefined ? (
          <div className="flex items-center gap-2 py-2 text-xs text-slate-400">
            <Loader2 className="h-3 w-3 animate-spin" />
            <span>Carico la massima…</span>
          </div>
        ) : preview ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider">
              <span className="rounded-full bg-[#D4A937]/15 px-2 py-0.5 font-mono text-[#F4C955]">
                {preview.court}
              </span>
              <span className="text-slate-500">n. {preview.ruling_number}</span>
              <span className="text-slate-600">·</span>
              <span className="text-slate-400">{preview.date}</span>
            </div>
            <p className="font-display text-sm font-bold text-white">{preview.headline}</p>
            <p className="text-[12px] leading-relaxed text-slate-300 line-clamp-4">
              {preview.principle}
            </p>
            <button
              type="button"
              onClick={handleClick}
              className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#F4C955] hover:underline"
            >
              <span>Apri nella Giurisprudenza</span>
              <ExternalLink className="h-3 w-3" />
            </button>
          </div>
        ) : (
          <p className="py-2 text-xs text-slate-400">
            Nessuna massima corrispondente nel database. Clic per cercare manualmente.
          </p>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}

/**
 * Renderer for a single assistant message body. Splits the text on inline
 * citations and intersperses {@link InlineCitationBadge} components.
 */
export function RenderedMessageBody({ text, parsed }) {
  if (!parsed || parsed.length === 0) {
    return <span className="whitespace-pre-wrap">{text}</span>;
  }
  return (
    <span className="whitespace-pre-wrap">
      {parsed.map((part, i) =>
        part.type === "text" ? (
          <React.Fragment key={i}>{part.text}</React.Fragment>
        ) : (
          <InlineCitationBadge
            key={i}
            text={part.text}
            kind={part.kind}
            query={part.query}
            dataTestId={`msg-citation-${i}`}
          />
        )
      )}
    </span>
  );
}
